'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Infinity, Zap, CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
  name: string;
}

interface HowItWorksSectionProps {
  plan: Plan;
}

export function HowItWorksSection({ plan }: HowItWorksSectionProps) {
  const t = useTranslations('planDetails.monolinePlan.howItWorks');

  const highlights = [
    {
      icon: Infinity,
      textKey: 'highlight1',
      color: 'primary',
    },
    {
      icon: Zap,
      textKey: 'highlight2',
      color: 'blue',
    },
    {
      icon: CheckCircle2,
      textKey: 'highlight3',
      color: 'green',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      primary: {
        bg: 'bg-primary-100 dark:bg-primary-900/30',
        icon: 'text-primary-600 dark:text-primary-400',
        border: 'border-primary-300 dark:border-primary-700',
      },
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        icon: 'text-blue-600 dark:text-blue-400',
        border: 'border-blue-300 dark:border-blue-700',
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        icon: 'text-green-600 dark:text-green-400',
        border: 'border-green-300 dark:border-green-700',
      },
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  return (
    <SectionWrapper ref={sectionRef.ref} variant="default" padding="lg" className="relative bg-white dark:bg-gray-950">
      <div className="container">
        <div className={cn(
          "text-center mb-16 transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          <Typography variant="h2" className="mb-4 text-gray-900 dark:text-white">
            {t('title')}
          </Typography>
        </div>

        <div className={cn(
          "max-w-5xl mx-auto transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        )} style={{ transitionDelay: '200ms' }}>
          <div className="relative bg-gradient-to-br from-gray-50 to-blue-50/50 dark:from-gray-900 dark:to-blue-950/20 rounded-2xl p-8 md:p-10 border-2 border-gray-200 dark:border-gray-800 shadow-xl">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-24 h-24 bg-primary-500/10 dark:bg-primary-500/20 rounded-br-full"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-blue-500/10 dark:bg-blue-500/20 rounded-tl-full"></div>

            <div className="relative z-10 space-y-8">
              {/* Paragraph */}
              <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                {t('description')}
              </Typography>

              {/* Highlights below the paragraph */}
              <div className="grid md:grid-cols-3 gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                {highlights.map((highlight, index) => {
                  const Icon = highlight.icon;
                  const colors = getColorClasses(highlight.color);
                  return (
                    <div
                      key={index}
                      className={cn(
                        `relative bg-white dark:bg-gray-800 rounded-xl p-6 border-2 ${colors.border} ${colors.bg} transition-all duration-700 hover:shadow-lg hover:-translate-y-1`,
                        sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                      )}
                      style={{ transitionDelay: `${400 + index * 100}ms` }}
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${colors.icon}`} />
                        </div>
                        <Typography variant="p" className="font-semibold text-gray-900 dark:text-white">
                          {t(highlight.textKey)}
                        </Typography>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

